import { api } from "@/lib/api";

// Helper: convert a urlsafe-base64 VAPID public key to a Uint8Array (browser requirement).
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export async function getCurrentPushPermission() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function getCurrentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function enableAdminPush() {
  if (!pushSupported()) throw new Error("This browser doesn't support push notifications. On iPhone, add this site to your Home Screen first.");
  const reg = await navigator.serviceWorker.ready;
  // Request permission (no-op if already granted)
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permission denied. Enable notifications for this site in your browser settings.");
  // Fetch the server's VAPID public key
  const { data } = await api.get("/push/vapid-public-key");
  const appKey = urlBase64ToUint8Array(data.public_key);
  // Re-use the existing browser subscription if it matches the current server VAPID key,
  // otherwise tear it down and subscribe fresh (handles the case where the server's keys
  // rotated, the previous subscribe POST 404'd, or iOS cached a stale endpoint).
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    const existingKey = sub.options && sub.options.applicationServerKey
      ? new Uint8Array(sub.options.applicationServerKey)
      : null;
    const sameKey = existingKey && existingKey.length === appKey.length
      && existingKey.every((b, i) => b === appKey[i]);
    if (!sameKey) {
      try { await sub.unsubscribe(); } catch (_) { /* best-effort */ }
      sub = null;
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appKey,
    });
  }
  // Always (re-)persist the subscription server-side. This is idempotent — the backend
  // upserts by endpoint — so calling Enable twice in a row safely heals a stale server.
  await api.post("/admin/push/subscribe", {
    endpoint: sub.endpoint,
    keys: sub.toJSON().keys,
  });
  return sub;
}

export async function disableAdminPush() {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  try { await api.post("/admin/push/unsubscribe", { endpoint: sub.endpoint }); } catch (e) { console.warn("server unsubscribe failed", e); }
  try { await sub.unsubscribe(); } catch (e) { console.warn("browser unsubscribe failed", e); }
}

export async function sendTestPush() {
  const { data } = await api.post("/admin/push/test");
  return data; // { sent, total, errors, message? }
}
